import { useState, useEffect, useMemo, useCallback } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle, CardFooter } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Separator } from "@/components/ui/separator";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { ScrollArea } from "@/components/ui/scroll-area";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/contexts/AuthContext";
import { useCompany } from "@/contexts/CompanyContext";
import { useNavigate } from "react-router-dom";
import {
  Building2, FileText, UserPlus, BookOpen, Check, Sparkles, ArrowRight,
  ArrowLeft, Plus, Trash2, CheckCircle2, AlertCircle, AlertTriangle, Info,
  Bot, RefreshCw, Send, Layers, Tag, Package, Users, Store, Factory,
  ShoppingCart, Truck, Utensils, Laptop, Stethoscope, Scissors, Wrench,
  Globe, Flame, Briefcase, HelpCircle, ShieldCheck, CheckSquare, Square
} from "lucide-react";
import {
  INDIAN_STATES_WITH_GST_CODES,
  PAN_ENTITY_TYPES,
  validatePAN,
  validateGSTIN,
  extractFromGSTIN,
  crossCheckGSTIN,
  getStateByCode,
  getStateByName,
} from "@/lib/onboarding/gstUtils";
import {
  INDUSTRY_TEMPLATES,
  INDUSTRY_CATEGORIES,
  getIndustryTemplate,
  buildChartOfAccounts,
  IndustryTemplate,
} from "@/lib/onboarding/industryTemplates";

// Icon mapping helper
function getIndustryIcon(iconName: string) {
  switch (iconName) {
    case "Flame": return Flame;
    case "Recycle":
    case "Layers": return Layers;
    case "ShoppingCart": return ShoppingCart;
    case "Sparkles": return Sparkles;
    case "Laptop": return Laptop;
    case "Scissors": return Scissors;
    case "Store": return Store;
    case "Factory": return Factory;
    case "Building2": return Building2;
    case "Wrench": return Wrench;
    case "Stethoscope": return Stethoscope;
    case "Globe": return Globe;
    case "Briefcase": return Briefcase;
    case "Utensils": return Utensils;
    case "Truck": return Truck;
    default: return Building2;
  }
}

const STEPS = [
  { id: 1, title: "Business Identity", subtitle: "Entity & Contact", icon: Building2 },
  { id: 2, title: "Tax & GST", subtitle: "GSTIN & PAN Intelligence", icon: FileText },
  { id: 3, title: "Industry Profile", subtitle: "19 Sector Templates", icon: Layers },
  { id: 4, title: "Accounting Setup", subtitle: "Chart of Accounts Preview", icon: BookOpen },
  { id: 5, title: "Team & Roles", subtitle: "Optional Access", icon: UserPlus },
  { id: 6, title: "Review & Launch", subtitle: "One-Click Activation", icon: CheckCircle2 },
];

const CONSTITUTIONS = [
  { value: "Sole Proprietorship", label: "Sole Proprietorship", panChar: "P" },
  { value: "Partnership Firm", label: "Partnership Firm", panChar: "F" },
  { value: "Limited Liability Partnership (LLP)", label: "Limited Liability Partnership (LLP)", panChar: "F" },
  { value: "Private Limited Company", label: "Private Limited Company (Pvt Ltd)", panChar: "C" },
  { value: "Public Limited Company", label: "Public Limited Company (Ltd)", panChar: "C" },
  { value: "Trust / Society / NGO", label: "Trust / Society / NGO", panChar: "T" },
  { value: "Individual / Freelancer", label: "Individual / Freelancer", panChar: "P" },
];

export default function OnboardingPage() {
  const { toast } = useToast();
  const { user, refreshUser } = useAuth();
  const { activeCompany, refreshCompanies } = useCompany();
  const navigate = useNavigate();

  // Redirect to dashboard if user already has an active company with completed onboarding
  useEffect(() => {
    if (activeCompany && activeCompany.onboardingCompleted) {
      navigate("/dashboard", { replace: true });
    }
  }, [activeCompany, navigate]);

  const [currentStep, setCurrentStep] = useState(1);
  const [submitting, setSubmitting] = useState(false);
  const [lastSaved, setLastSaved] = useState<string | null>(null);

  // AI Assistant Drawer / Dialog State
  const [aiOpen, setAiOpen] = useState(false);
  const [aiPrompt, setAiPrompt] = useState("");
  const [aiResponse, setAiResponse] = useState<string | null>(null);
  const [aiLoading, setAiLoading] = useState(false);

  // Step 1: Business Identity
  const [identityData, setIdentityData] = useState({
    name: "",
    tradeName: "",
    businessConstitution: "Sole Proprietorship",
    ownerName: user?.name || "",
    phone: "",
    email: user?.email || "",
    website: "",
    address: "",
    city: "",
    district: "",
    state: "Maharashtra",
    stateCode: "27",
    pincode: "",
    financialYearStart: "04",
    baseCurrency: "INR",
  });

  // Step 2: Tax Registration & GST Intelligence
  const [taxData, setTaxData] = useState({
    gstRegistered: true,
    gstin: "",
    pan: "",
    compositionScheme: false,
  });

  // Step 3: Industry & Business Profile Selection
  const [selectedIndustryId, setSelectedIndustryId] = useState<string>("iron_steel");
  const [industrySearch, setIndustrySearch] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("All");

  // Step 4: Accounting Configuration (Ledgers & Stock Groups)
  const [coaLedgers, setCoaLedgers] = useState<
    { name: string; group: string; balanceType: "Dr" | "Cr"; openingBalance: number; enabled: boolean; isCustom?: boolean }[]
  >([]);
  const [customLedgerName, setCustomLedgerName] = useState("");
  const [customLedgerGroup, setCustomLedgerGroup] = useState("Indirect Expenses");

  // Step 5: Team & Access Setup (Optional)
  const [teamMembers, setTeamMembers] = useState<
    { name: string; email: string; role: string }[]
  >([]);

  // Load draft from local cache or DB on mount
  useEffect(() => {
    try {
      const cacheKey = `accoupro_onboarding_draft_${user?.id || "guest"}`;
      const saved = localStorage.getItem(cacheKey);
      if (saved) {
        const parsed = JSON.parse(saved);
        if (parsed.identityData) setIdentityData(parsed.identityData);
        if (parsed.taxData) setTaxData(parsed.taxData);
        if (parsed.selectedIndustryId) setSelectedIndustryId(parsed.selectedIndustryId);
        if (parsed.teamMembers) setTeamMembers(parsed.teamMembers);
        if (parsed.step) setCurrentStep(parsed.step);
      } else if (activeCompany) {
        // Pre-fill from active company if any
        setIdentityData((prev) => ({
          ...prev,
          name: activeCompany.name || prev.name,
          tradeName: activeCompany.tradeName || prev.tradeName || activeCompany.name,
          ownerName: activeCompany.ownerName || user?.name || prev.ownerName,
          phone: activeCompany.phone || prev.phone,
          email: activeCompany.email || user?.email || prev.email,
          address: activeCompany.address || prev.address,
          city: activeCompany.city || prev.city,
          state: activeCompany.state || prev.state,
          pincode: activeCompany.pincode || prev.pincode,
          stateCode: activeCompany.stateCode || prev.stateCode,
        }));
        if (activeCompany.gstin) {
          setTaxData((prev) => ({
            ...prev,
            gstRegistered: activeCompany.gstRegistered,
            gstin: activeCompany.gstin,
            pan: activeCompany.pan || prev.pan,
            compositionScheme: activeCompany.compositionScheme,
          }));
        }
      }
    } catch (e) {
      console.warn("Failed to read onboarding draft", e);
    }
  }, [user?.id, activeCompany]);

  // Sync stateCode when state changes in Step 1
  const handleStateChange = (stateName: string) => {
    const st = getStateByName(stateName);
    setIdentityData((prev) => ({
      ...prev,
      state: stateName,
      stateCode: st ? st.code : prev.stateCode,
    }));
  };

  // Sync PAN & State when GSTIN changes in Step 2
  const handleGSTINChange = (val: string) => {
    const cleanGstin = val.toUpperCase().trim();
    setTaxData((prev) => {
      const updated = { ...prev, gstin: cleanGstin };
      const extracted = extractFromGSTIN(cleanGstin);
      if (extracted.pan && (!prev.pan || prev.pan === prev.gstin.slice(2, 12))) {
        updated.pan = extracted.pan;
      }
      return updated;
    });

    // Auto-suggest State if 2-digit code detected
    if (cleanGstin.length >= 2) {
      const code = cleanGstin.slice(0, 2);
      const st = getStateByCode(code);
      if (st && st.name !== identityData.state) {
        // We will provide a 1-click update banner
      }
    }
  };

  // When industry, constitution, or GST status changes, regenerate Step 4 Chart of Accounts
  useEffect(() => {
    const template = getIndustryTemplate(selectedIndustryId);
    const generated = buildChartOfAccounts(template, identityData.businessConstitution, taxData.gstRegistered);
    setCoaLedgers((prev) => {
      // Preserve any custom ledgers user manually added
      const customOnes = prev.filter((p) => p.isCustom);
      return [
        ...generated.map((g) => ({
          name: g.name,
          group: g.group,
          balanceType: (g.type === "Cr" ? "Cr" : "Dr") as "Dr" | "Cr",
          openingBalance: 0,
          enabled: true,
        })),
        ...customOnes,
      ];
    });
  }, [selectedIndustryId, identityData.businessConstitution, taxData.gstRegistered]);

  // Autosave to localStorage on state changes
  useEffect(() => {
    const cacheKey = `accoupro_onboarding_draft_${user?.id || "guest"}`;
    const payload = {
      step: currentStep,
      identityData,
      taxData,
      selectedIndustryId,
      teamMembers,
      updatedAt: new Date().toISOString(),
    };
    try {
      localStorage.setItem(cacheKey, JSON.stringify(payload));
      setLastSaved(new Date().toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }));
    } catch {}
  }, [currentStep, identityData, taxData, selectedIndustryId, teamMembers, user?.id]);

  // GST & PAN cross check warnings
  const gstValidation = useMemo(() => {
    if (!taxData.gstRegistered) return { isValid: true, warnings: [], extracted: null };
    return {
      isValid: validateGSTIN(taxData.gstin),
      warnings: crossCheckGSTIN(
        taxData.gstin,
        taxData.pan,
        identityData.state,
        identityData.businessConstitution
      ),
      extracted: extractFromGSTIN(taxData.gstin),
    };
  }, [taxData.gstRegistered, taxData.gstin, taxData.pan, identityData.state, identityData.businessConstitution]);

  // PAN Entity badge
  const panEntityLabel = useMemo(() => {
    if (taxData.pan && taxData.pan.length >= 4) {
      const char = taxData.pan.charAt(3).toUpperCase();
      return PAN_ENTITY_TYPES[char] || "General Entity";
    }
    return null;
  }, [taxData.pan]);

  // Industry catalog filtered
  const filteredIndustries = useMemo(() => {
    return INDUSTRY_TEMPLATES.filter((tmpl) => {
      const matchesCategory = selectedCategory === "All" || tmpl.category === selectedCategory;
      const searchLower = industrySearch.toLowerCase().trim();
      const matchesSearch =
        !searchLower ||
        tmpl.name.toLowerCase().includes(searchLower) ||
        tmpl.description.toLowerCase().includes(searchLower) ||
        tmpl.tags.some((t) => t.toLowerCase().includes(searchLower));
      return matchesCategory && matchesSearch;
    });
  }, [selectedCategory, industrySearch]);

  const activeTemplate = useMemo(() => {
    return getIndustryTemplate(selectedIndustryId);
  }, [selectedIndustryId]);

  // Step navigation validation
  const validateCurrentStep = (): boolean => {
    if (currentStep === 1) {
      if (!identityData.name.trim()) {
        toast({ title: "Company Name Required", description: "Please enter your business or company name.", variant: "destructive" });
        return false;
      }
      if (!identityData.state) {
        toast({ title: "State Required", description: "Please select the registered state for your business.", variant: "destructive" });
        return false;
      }
      if (identityData.phone && identityData.phone.replace(/\D/g, "").length !== 10) {
        toast({ title: "Invalid Phone", description: "Please enter a valid 10-digit Indian phone number.", variant: "destructive" });
        return false;
      }
      if (identityData.pincode && identityData.pincode.replace(/\D/g, "").length !== 6) {
        toast({ title: "Invalid PIN Code", description: "Please enter a valid 6-digit Indian PIN code.", variant: "destructive" });
        return false;
      }
      return true;
    }

    if (currentStep === 2) {
      if (taxData.gstRegistered) {
        if (!taxData.gstin.trim()) {
          toast({ title: "GSTIN Required", description: "Please enter your 15-character GSTIN or toggle GST to Unregistered.", variant: "destructive" });
          return false;
        }
        if (taxData.gstin.trim().length !== 15) {
          toast({ title: "Invalid GSTIN", description: "GSTIN must be exactly 15 characters long.", variant: "destructive" });
          return false;
        }
      }
      if (taxData.pan && !validatePAN(taxData.pan)) {
        toast({ title: "Invalid PAN Format", description: "PAN must follow standard 10-character format (e.g., ABCDE1234F).", variant: "destructive" });
        return false;
      }
      return true;
    }

    if (currentStep === 3) {
      if (!selectedIndustryId) {
        toast({ title: "Select Industry", description: "Please choose an industry template tailored for your business.", variant: "destructive" });
        return false;
      }
      return true;
    }

    if (currentStep === 4) {
      const enabledLedgers = coaLedgers.filter((l) => l.enabled);
      if (enabledLedgers.length === 0) {
        toast({ title: "Chart of Accounts Empty", description: "Please enable at least one ledger account.", variant: "destructive" });
        return false;
      }
      return true;
    }

    return true;
  };

  const handleNext = () => {
    if (validateCurrentStep()) {
      if (currentStep < 6) setCurrentStep(currentStep + 1);
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  };

  const handleBack = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  };

  // Add custom ledger in Step 4
  const handleAddCustomLedger = () => {
    if (!customLedgerName.trim()) return;
    const exists = coaLedgers.some((l) => l.name.toLowerCase() === customLedgerName.trim().toLowerCase());
    if (exists) {
      toast({ title: "Ledger Exists", description: "A ledger with this name already exists in the Chart of Accounts.", variant: "destructive" });
      return;
    }

    setCoaLedgers((prev) => [
      ...prev,
      {
        name: customLedgerName.trim(),
        group: customLedgerGroup,
        balanceType: customLedgerGroup.includes("Expense") || customLedgerGroup.includes("Debtors") || customLedgerGroup.includes("Bank") ? "Dr" : "Cr",
        openingBalance: 0,
        enabled: true,
        isCustom: true,
      },
    ]);
    setCustomLedgerName("");
    toast({ title: "Ledger Added", description: `"${customLedgerName.trim()}" added to ${customLedgerGroup}.` });
  };

  // Add / remove team member in Step 5
  const handleAddTeamMember = () => {
    setTeamMembers((prev) => [...prev, { name: "", email: "", role: "accountant" }]);
  };

  const handleRemoveTeamMember = (index: number) => {
    setTeamMembers((prev) => prev.filter((_, idx) => idx !== index));
  };

  const handleUpdateTeamMember = (index: number, field: string, value: string) => {
    setTeamMembers((prev) =>
      prev.map((m, idx) => (idx === index ? { ...m, [field]: value } : m))
    );
  };

  // AI Assistant Query Handler
  const handleAskAI = async (customPrompt?: string) => {
    const promptToSend = customPrompt || aiPrompt;
    if (!promptToSend.trim()) return;

    setAiLoading(true);
    setAiResponse(null);

    try {
      const res = await fetch("/api/onboarding/ai-assist", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          prompt: promptToSend,
          step: currentStep,
          industry: activeTemplate?.name,
          context: {
            constitution: identityData.businessConstitution,
            gstin: taxData.gstin,
            pan: taxData.pan,
            state: identityData.state,
          },
        }),
      });

      const json = await res.json();
      if (json.success && json.answer) {
        setAiResponse(json.answer);
      } else {
        setAiResponse("AccouPro AI assistant is ready to help. Please select an industry template or specify your business type.");
      }
    } catch {
      setAiResponse("AccouPro AI is currently operating in offline advisory mode. All standard Indian accounting rules, GST rates, and Chart of Accounts templates are pre-configured accurately.");
    } finally {
      setAiLoading(false);
    }
  };

  // Final Submission to /api/onboarding/complete
  const handleCompleteOnboarding = async () => {
    if (!validateCurrentStep()) return;

    setSubmitting(true);
    try {
      const activeLedgers = coaLedgers.filter((l) => l.enabled);
      const validTeamMembers = teamMembers.filter((m) => m.email.trim() && m.name.trim());

      const payload = {
        company: {
          id: activeCompany?.id,
          name: identityData.name.trim(),
          tradeName: identityData.tradeName.trim() || identityData.name.trim(),
          legalName: identityData.name.trim(),
          businessConstitution: identityData.businessConstitution,
          industry: activeTemplate.id,
          industryLabel: activeTemplate.name,
          ownerName: identityData.ownerName.trim(),
          phone: identityData.phone.trim(),
          email: identityData.email.trim(),
          website: identityData.website.trim(),
          address: identityData.address.trim(),
          city: identityData.city.trim(),
          district: identityData.district.trim(),
          state: identityData.state.trim(),
          stateCode: identityData.stateCode,
          pincode: identityData.pincode.trim(),
          financialYearStart: identityData.financialYearStart,
          baseCurrency: identityData.baseCurrency,
          gstRegistered: taxData.gstRegistered,
          gstin: taxData.gstRegistered ? taxData.gstin.trim() : null,
          pan: taxData.pan.trim() || (taxData.gstin.length >= 12 ? taxData.gstin.slice(2, 12) : null),
          compositionScheme: taxData.compositionScheme,
        },
        ledgers: activeLedgers.map((l) => ({
          name: l.name,
          group: l.group,
          balanceType: l.balanceType,
          openingBalance: 0,
        })),
        inventoryGroups: activeTemplate.stockGroups.map((g) => ({ name: g })),
        inventoryCategories: activeTemplate.stockCategories.map((c) => ({ name: c })),
        teamMembers: validTeamMembers,
      };

      // Retrieve auth headers
      const sessionStr = localStorage.getItem("accoupro_auth_session");
      const userStr = localStorage.getItem("accoupro_auth_user");
      const headers: Record<string, string> = { "Content-Type": "application/json" };
      if (sessionStr) {
        try {
          const sess = JSON.parse(sessionStr);
          if (sess.access_token) headers["Authorization"] = `Bearer ${sess.access_token}`;
        } catch {}
      }
      if (userStr) {
        try {
          const u = JSON.parse(userStr);
          if (u.id) headers["x-user-id"] = u.id;
          if (u.role) headers["x-user-role"] = u.role;
        } catch {}
      }

      const res = await fetch("/api/onboarding/complete", {
        method: "POST",
        headers,
        body: JSON.stringify(payload),
      });

      const data = await res.json();
      if (!res.ok || !data.success) {
        throw new Error(data.error || "Failed to complete company setup");
      }

      const finalCompanyId = data.companyId || data.data?.companyId;

      // Update cached session with new company_id
      if (userStr && finalCompanyId) {
        try {
          const u = JSON.parse(userStr);
          u.company = finalCompanyId;
          u.company_id = finalCompanyId;
          localStorage.setItem("accoupro_auth_user", JSON.stringify(u));
        } catch {}
      }

      // Clear draft cache
      const cacheKey = `accoupro_onboarding_draft_${user?.id || "guest"}`;
      localStorage.removeItem(cacheKey);

      // Refresh user and company context
      if (refreshUser) {
        await refreshUser();
      }
      await refreshCompanies();

      toast({
        title: "🎉 Onboarding Completed Successfully!",
        description: `Welcome to AccouPro! Your ${activeTemplate.name} accounting workspace is live with ${data.createdLedgersCount || activeLedgers.length} pre-configured ledgers.`,
      });

      // Smooth redirect to dashboard
      setTimeout(() => {
        navigate("/dashboard", { replace: true });
      }, 500);
    } catch (err: any) {
      console.error("[Onboarding Complete Error]:", err);
      toast({
        title: "Setup Error",
        description: err.message || "Could not complete setup. Please check details and try again.",
        variant: "destructive",
      });
    } finally {
      setSubmitting(false);
    }
  };

  const progressPercent = Math.round(((currentStep - 1) / (STEPS.length - 1)) * 100);

  return (
    <div className="min-h-screen bg-gradient-to-b from-background via-muted/20 to-background text-foreground py-6 px-3 sm:px-6 lg:px-8">
      <div className="max-w-5xl mx-auto space-y-6">
        
        {/* Top Header & AI Assistant Button */}
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 pb-2 border-b border-border/60">
          <div className="flex items-center gap-3">
            <div className="w-11 h-11 rounded-xl bg-primary/10 border border-primary/20 flex items-center justify-center text-primary shadow-soft">
              <Building2 className="w-6 h-6" />
            </div>
            <div>
              <div className="flex items-center gap-2">
                <h1 className="text-xl sm:text-2xl font-bold font-display tracking-tight">AccouPro Onboarding</h1>
                <Badge variant="outline" className="text-xs bg-primary/5 text-primary border-primary/20">
                  Self-Configuring ERP
                </Badge>
              </div>
              <p className="text-xs sm:text-sm text-muted-foreground">
                Intelligent, industry-tailored accounting configuration with zero manual ledger entry
              </p>
            </div>
          </div>

          <div className="flex items-center gap-2.5 self-end sm:self-auto">
            {lastSaved && (
              <span className="text-[11px] text-muted-foreground flex items-center gap-1 hidden sm:inline-flex">
                <Check className="w-3 h-3 text-emerald-500" /> Autosaved {lastSaved}
              </span>
            )}
            <Dialog open={aiOpen} onOpenChange={setAiOpen}>
              <DialogTrigger asChild>
                <Button variant="outline" size="sm" className="gap-2 border-primary/30 text-primary hover:bg-primary/5 font-medium shadow-sm">
                  <Sparkles className="w-4 h-4 text-primary" />
                  <span>Ask AccouPro AI</span>
                </Button>
              </DialogTrigger>
              <DialogContent className="sm:max-w-xl">
                <DialogHeader>
                  <DialogTitle className="flex items-center gap-2 text-primary">
                    <Bot className="w-5 h-5" /> AccouPro AI Advisor
                  </DialogTitle>
                  <DialogDescription>
                    Context-aware guidance for GST compliance, industry chart of accounts, and accounting structure. Powered by Gemma-4.
                  </DialogDescription>
                </DialogHeader>

                <div className="space-y-4 py-2">
                  <div className="flex flex-wrap gap-1.5">
                    {[
                      "What is my 2-digit GST state code?",
                      "Scrap Metal vs Manufacturing template?",
                      "Do I need RCM tax ledgers?",
                      "Difference between LLP and Pvt Ltd PAN?",
                    ].map((prompt, idx) => (
                      <Button
                        key={idx}
                        variant="secondary"
                        size="sm"
                        className="text-xs h-7 py-1 px-2.5 rounded-full"
                        onClick={() => {
                          setAiPrompt(prompt);
                          handleAskAI(prompt);
                        }}
                      >
                        {prompt}
                      </Button>
                    ))}
                  </div>

                  <div className="flex gap-2">
                    <Input
                      placeholder="Ask any question about GST, industry, ledgers, or constitution..."
                      value={aiPrompt}
                      onChange={(e) => setAiPrompt(e.target.value)}
                      onKeyDown={(e) => e.key === "Enter" && handleAskAI()}
                    />
                    <Button onClick={() => handleAskAI()} disabled={aiLoading || !aiPrompt.trim()} className="gap-1.5 shrink-0">
                      {aiLoading ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                      <span>Ask</span>
                    </Button>
                  </div>

                  {aiResponse && (
                    <div className="p-3.5 rounded-lg bg-muted/60 border border-border text-sm leading-relaxed max-h-60 overflow-y-auto whitespace-pre-line">
                      {aiResponse}
                    </div>
                  )}
                </div>
              </DialogContent>
            </Dialog>
          </div>
        </div>

        {/* Wizard Stepper Progress Bar */}
        <div className="space-y-3 bg-card border border-border rounded-xl p-4 sm:p-5 shadow-card">
          <div className="flex items-center justify-between text-xs sm:text-sm font-medium">
            <span className="text-muted-foreground">
              Step {currentStep} of {STEPS.length}: <strong className="text-foreground">{STEPS[currentStep - 1].title}</strong>
            </span>
            <span className="text-primary font-semibold">{progressPercent}% Completed</span>
          </div>
          <Progress value={progressPercent} className="h-2" />

          {/* Stepper Tabs */}
          <div className="grid grid-cols-3 sm:grid-cols-6 gap-2 pt-2">
            {STEPS.map((step) => {
              const Icon = step.icon;
              const isPast = step.id < currentStep;
              const isCurrent = step.id === currentStep;

              return (
                <button
                  key={step.id}
                  type="button"
                  onClick={() => {
                    if (step.id < currentStep) setCurrentStep(step.id);
                  }}
                  disabled={step.id > currentStep}
                  className={`text-left p-2 rounded-lg border transition-all flex flex-col items-start gap-1 ${
                    isCurrent
                      ? "border-primary bg-primary/5 shadow-sm"
                      : isPast
                      ? "border-border/60 bg-muted/30 hover:bg-muted/50 cursor-pointer"
                      : "border-transparent opacity-50 cursor-not-allowed"
                  }`}
                >
                  <div className="flex items-center gap-1.5 w-full">
                    <span
                      className={`w-5 h-5 rounded-full flex items-center justify-center text-[10px] font-bold ${
                        isPast
                          ? "bg-emerald-500 text-white"
                          : isCurrent
                          ? "bg-primary text-primary-foreground"
                          : "bg-muted text-muted-foreground"
                      }`}
                    >
                      {isPast ? <Check className="w-3 h-3" /> : step.id}
                    </span>
                    <span className="text-[11px] font-semibold truncate hidden sm:inline">{step.title}</span>
                  </div>
                  <span className="text-[10px] text-muted-foreground truncate w-full hidden md:block">
                    {step.subtitle}
                  </span>
                </button>
              );
            })}
          </div>
        </div>

        {/* STEP 1: BUSINESS IDENTITY & LEGAL ENTITY */}
        {currentStep === 1 && (
          <Card className="border-border shadow-card">
            <CardHeader>
              <div className="flex items-center gap-2 text-primary font-semibold text-sm">
                <Building2 className="w-4 h-4" /> Step 1 of 6
              </div>
              <CardTitle className="text-xl sm:text-2xl font-bold font-display">Business Identity & Legal Entity</CardTitle>
              <CardDescription>
                Provide your primary business details, legal constitution, and registered office location.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="comp-name" className="text-xs font-semibold uppercase tracking-wider">
                    Legal Business Name <span className="text-destructive">*</span>
                  </Label>
                  <Input
                    id="comp-name"
                    placeholder="e.g. Apex Industrial Solutions Pvt Ltd"
                    value={identityData.name}
                    onChange={(e) => setIdentityData({ ...identityData, name: e.target.value })}
                  />
                  <p className="text-[11px] text-muted-foreground">Official registered name appearing on financial statements</p>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="comp-trade" className="text-xs font-semibold uppercase tracking-wider">
                    Trade / Brand Name (Optional)
                  </Label>
                  <Input
                    id="comp-trade"
                    placeholder="e.g. Apex Metals"
                    value={identityData.tradeName}
                    onChange={(e) => setIdentityData({ ...identityData, tradeName: e.target.value })}
                  />
                  <p className="text-[11px] text-muted-foreground">Customer-facing trading brand shown on invoices</p>
                </div>

                <div className="space-y-2">
                  <Label className="text-xs font-semibold uppercase tracking-wider">
                    Business Constitution <span className="text-destructive">*</span>
                  </Label>
                  <Select
                    value={identityData.businessConstitution}
                    onValueChange={(val) => setIdentityData({ ...identityData, businessConstitution: val })}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select Constitution" />
                    </SelectTrigger>
                    <SelectContent>
                      {CONSTITUTIONS.map((c) => (
                        <SelectItem key={c.value} value={c.value}>
                          <div className="flex items-center justify-between gap-4">
                            <span>{c.label}</span>
                            <Badge variant="outline" className="text-[10px] font-mono">PAN: '{c.panChar}'</Badge>
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="comp-owner" className="text-xs font-semibold uppercase tracking-wider">
                    Authorized Signatory / Owner Name
                  </Label>
                  <Input
                    id="comp-owner"
                    placeholder="e.g. Rajesh Kumar"
                    value={identityData.ownerName}
                    onChange={(e) => setIdentityData({ ...identityData, ownerName: e.target.value })}
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="comp-phone" className="text-xs font-semibold uppercase tracking-wider">
                    Official Mobile / Contact Phone
                  </Label>
                  <Input
                    id="comp-phone"
                    placeholder="e.g. 9876543210"
                    maxLength={10}
                    value={identityData.phone}
                    onChange={(e) => setIdentityData({ ...identityData, phone: e.target.value.replace(/\D/g, "") })}
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="comp-email" className="text-xs font-semibold uppercase tracking-wider">
                    Official Billing Email
                  </Label>
                  <Input
                    id="comp-email"
                    type="email"
                    placeholder="accounts@apexsolutions.in"
                    value={identityData.email}
                    onChange={(e) => setIdentityData({ ...identityData, email: e.target.value })}
                  />
                </div>
              </div>

              <Separator />

              <div>
                <h3 className="text-sm font-semibold mb-3">Registered Office Address</h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="md:col-span-3 space-y-2">
                    <Label htmlFor="comp-addr" className="text-xs font-semibold uppercase tracking-wider">
                      Address (Plot / Street / Building)
                    </Label>
                    <Input
                      id="comp-addr"
                      placeholder="e.g. Plot No 42, MIDC Industrial Area, Phase II"
                      value={identityData.address}
                      onChange={(e) => setIdentityData({ ...identityData, address: e.target.value })}
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="comp-city" className="text-xs font-semibold uppercase tracking-wider">
                      City / Town
                    </Label>
                    <Input
                      id="comp-city"
                      placeholder="e.g. Navi Mumbai"
                      value={identityData.city}
                      onChange={(e) => setIdentityData({ ...identityData, city: e.target.value })}
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="comp-dist" className="text-xs font-semibold uppercase tracking-wider">
                      District
                    </Label>
                    <Input
                      id="comp-dist"
                      placeholder="e.g. Thane"
                      value={identityData.district}
                      onChange={(e) => setIdentityData({ ...identityData, district: e.target.value })}
                    />
                  </div>

                  <div className="space-y-2">
                    <Label className="text-xs font-semibold uppercase tracking-wider">
                      State & 2-Digit GST Code <span className="text-destructive">*</span>
                    </Label>
                    <Select value={identityData.state} onValueChange={handleStateChange}>
                      <SelectTrigger>
                        <SelectValue placeholder="Select State" />
                      </SelectTrigger>
                      <SelectContent className="max-h-60">
                        {INDIAN_STATES_WITH_GST_CODES.map((s) => (
                          <SelectItem key={s.code} value={s.name}>
                            <div className="flex items-center justify-between gap-3 w-full">
                              <span>{s.name}</span>
                              <Badge variant="outline" className="text-[10px] font-mono">Code {s.code}</Badge>
                            </div>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="comp-pin" className="text-xs font-semibold uppercase tracking-wider">
                      PIN Code (6 Digits)
                    </Label>
                    <Input
                      id="comp-pin"
                      placeholder="e.g. 400705"
                      maxLength={6}
                      value={identityData.pincode}
                      onChange={(e) => setIdentityData({ ...identityData, pincode: e.target.value.replace(/\D/g, "") })}
                    />
                  </div>

                  <div className="space-y-2">
                    <Label className="text-xs font-semibold uppercase tracking-wider">
                      Financial Year Start
                    </Label>
                    <Select
                      value={identityData.financialYearStart}
                      onValueChange={(val) => setIdentityData({ ...identityData, financialYearStart: val })}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="04">1st April (Standard Indian Financial Year)</SelectItem>
                        <SelectItem value="01">1st January (Calendar Year)</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </div>
            </CardContent>
            <CardFooter className="flex justify-between border-t border-border/60 pt-4">
              <div className="text-xs text-muted-foreground">
                All data is encrypted and multi-tenant isolated
              </div>
              <Button onClick={handleNext} className="gap-2">
                <span>Continue to Tax & GST</span>
                <ArrowRight className="w-4 h-4" />
              </Button>
            </CardFooter>
          </Card>
        )}

        {/* STEP 2: TAX REGISTRATION & GST INTELLIGENCE */}
        {currentStep === 2 && (
          <Card className="border-border shadow-card">
            <CardHeader>
              <div className="flex items-center gap-2 text-primary font-semibold text-sm">
                <FileText className="w-4 h-4" /> Step 2 of 6
              </div>
              <CardTitle className="text-xl sm:text-2xl font-bold font-display">Tax Registration & GST Intelligence</CardTitle>
              <CardDescription>
                Real-time Indian State & 2-digit GST code auto-identification, PAN entity format verification, and GSTIN structure cross-checking.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* GST Toggle Card */}
              <div className="p-4 rounded-xl border border-border bg-card flex items-center justify-between gap-4">
                <div className="space-y-1">
                  <div className="flex items-center gap-2">
                    <p className="font-semibold text-foreground text-sm sm:text-base">Is this business registered under GST?</p>
                    <Badge variant={taxData.gstRegistered ? "default" : "secondary"} className="text-xs">
                      {taxData.gstRegistered ? "GST Registered" : "Unregistered / Non-GST"}
                    </Badge>
                  </div>
                  <p className="text-xs text-muted-foreground">
                    {taxData.gstRegistered
                      ? "AccouPro will auto-configure CGST, SGST, IGST, and RCM tax accounts and HSN lookup."
                      : "Standard business accounting will be set up without GST ledgers. You can activate GST anytime."}
                  </p>
                </div>
                <Switch
                  checked={taxData.gstRegistered}
                  onCheckedChange={(checked) => setTaxData({ ...taxData, gstRegistered: checked })}
                />
              </div>

              {taxData.gstRegistered ? (
                <div className="space-y-5">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {/* GSTIN Input */}
                    <div className="space-y-2">
                      <Label htmlFor="tax-gstin" className="text-xs font-semibold uppercase tracking-wider flex items-center justify-between">
                        <span>15-Character GSTIN <span className="text-destructive">*</span></span>
                        {gstValidation.isValid && (
                          <span className="text-xs text-emerald-600 font-medium flex items-center gap-1">
                            <CheckCircle2 className="w-3.5 h-3.5" /> Valid Format
                          </span>
                        )}
                      </Label>
                      <Input
                        id="tax-gstin"
                        placeholder="e.g. 27AAACA1234A1Z5"
                        maxLength={15}
                        className="font-mono uppercase text-base tracking-wider"
                        value={taxData.gstin}
                        onChange={(e) => handleGSTINChange(e.target.value)}
                      />
                      <p className="text-[11px] text-muted-foreground">
                        Structure: 2-digit State + 10-digit PAN + 1-digit Entity + 'Z' + Checksum
                      </p>
                    </div>

                    {/* Permanent Account Number (PAN) */}
                    <div className="space-y-2">
                      <Label htmlFor="tax-pan" className="text-xs font-semibold uppercase tracking-wider flex items-center justify-between">
                        <span>10-Digit PAN <span className="text-destructive">*</span></span>
                        {panEntityLabel && (
                          <Badge variant="outline" className="text-[10px] bg-primary/5 text-primary border-primary/20">
                            {panEntityLabel}
                          </Badge>
                        )}
                      </Label>
                      <Input
                        id="tax-pan"
                        placeholder="e.g. AAACA1234A"
                        maxLength={10}
                        className="font-mono uppercase text-base tracking-wider"
                        value={taxData.pan}
                        onChange={(e) => setTaxData({ ...taxData, pan: e.target.value.toUpperCase().trim() })}
                      />
                      <p className="text-[11px] text-muted-foreground">
                        Extracted automatically from GSTIN characters 3 to 12
                      </p>
                    </div>
                  </div>

                  {/* GSTIN Auto-Detection & State Cross-Check Card */}
                  {gstValidation.extracted && gstValidation.extracted.state && (
                    <div className="p-4 rounded-xl border border-primary/20 bg-primary/5 space-y-2">
                      <div className="flex items-center justify-between flex-wrap gap-2">
                        <div className="flex items-center gap-2">
                          <ShieldCheck className="w-5 h-5 text-primary" />
                          <span className="text-sm font-semibold text-foreground">
                            Detected State: <strong>{gstValidation.extracted.state.name}</strong> (Code {gstValidation.extracted.stateCode})
                          </span>
                        </div>
                        {gstValidation.extracted.state.name !== identityData.state && (
                          <Button
                            variant="outline"
                            size="sm"
                            className="text-xs h-7 gap-1 border-primary/40 text-primary hover:bg-primary/10"
                            onClick={() => {
                              handleStateChange(gstValidation.extracted!.state!.name);
                              toast({ title: "State Updated", description: `Business state set to ${gstValidation.extracted!.state!.name}` });
                            }}
                          >
                            Update State to {gstValidation.extracted.state.name}
                          </Button>
                        )}
                      </div>
                      <div className="text-xs text-muted-foreground flex flex-wrap gap-x-4 gap-y-1">
                        <span>PAN: <strong className="font-mono text-foreground">{gstValidation.extracted.pan}</strong></span>
                        <span>Entity Type: <strong className="text-foreground">{gstValidation.extracted.entityTypeLabel}</strong></span>
                        <span>Entity Code: <strong className="font-mono text-foreground">{gstValidation.extracted.entityNumber}</strong></span>
                      </div>
                    </div>
                  )}

                  {/* Cross-Check Warnings */}
                  {gstValidation.warnings.length > 0 && (
                    <div className="space-y-2">
                      {gstValidation.warnings.map((warn, i) => (
                        <div
                          key={i}
                          className={`p-3 rounded-lg border text-xs flex items-start gap-2.5 ${
                            warn.severity === "error"
                              ? "bg-destructive/10 border-destructive/30 text-destructive"
                              : warn.severity === "warning"
                              ? "bg-amber-500/10 border-amber-500/30 text-amber-700 dark:text-amber-400"
                              : "bg-blue-500/10 border-blue-500/30 text-blue-700 dark:text-blue-400"
                          }`}
                        >
                          {warn.severity === "error" ? (
                            <AlertCircle className="w-4 h-4 shrink-0 mt-0.5" />
                          ) : warn.severity === "warning" ? (
                            <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
                          ) : (
                            <Info className="w-4 h-4 shrink-0 mt-0.5" />
                          )}
                          <div>
                            <strong className="font-semibold block">{warn.message}</strong>
                            {warn.suggestion && <span className="text-[11px] opacity-90">{warn.suggestion}</span>}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Composition Scheme Toggle */}
                  <div className="p-3.5 rounded-lg border border-border bg-muted/20 flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-foreground">Composition Scheme (Optional)</p>
                      <p className="text-xs text-muted-foreground">
                        Simplified GST scheme for small traders and manufacturers (turnover under ₹1.5 Cr, 1% rate, no ITC)
                      </p>
                    </div>
                    <Switch
                      checked={taxData.compositionScheme}
                      onCheckedChange={(checked) => setTaxData({ ...taxData, compositionScheme: checked })}
                    />
                  </div>
                </div>
              ) : (
                <div className="p-4 rounded-xl border border-dashed border-border bg-muted/10 space-y-4">
                  <div className="flex gap-3">
                    <Info className="w-5 h-5 text-muted-foreground shrink-0 mt-0.5" />
                    <div>
                      <p className="text-sm font-medium text-foreground">Unregistered / Non-GST Accounting Mode</p>
                      <p className="text-xs text-muted-foreground mt-0.5">
                        AccouPro will configure clean, direct local accounting without GST input/output ledgers or tax splits. You can enter PAN for TDS compliance.
                      </p>
                    </div>
                  </div>
                  <div className="max-w-xs space-y-2">
                    <Label htmlFor="tax-pan-unreg" className="text-xs font-semibold uppercase tracking-wider">
                      Business PAN (Optional)
                    </Label>
                    <Input
                      id="tax-pan-unreg"
                      placeholder="e.g. AAACA1234A"
                      maxLength={10}
                      className="font-mono uppercase tracking-wider"
                      value={taxData.pan}
                      onChange={(e) => setTaxData({ ...taxData, pan: e.target.value.toUpperCase().trim() })}
                    />
                  </div>
                </div>
              )}
            </CardContent>
            <CardFooter className="flex justify-between border-t border-border/60 pt-4">
              <Button variant="outline" onClick={handleBack} className="gap-2">
                <ArrowLeft className="w-4 h-4" />
                <span>Back</span>
              </Button>
              <Button onClick={handleNext} className="gap-2">
                <span>Continue to Industry Profile</span>
                <ArrowRight className="w-4 h-4" />
              </Button>
            </CardFooter>
          </Card>
        )}

        {/* STEP 3: INDUSTRY & BUSINESS PROFILE SELECTION */}
        {currentStep === 3 && (
          <Card className="border-border shadow-card">
            <CardHeader>
              <div className="flex items-center gap-2 text-primary font-semibold text-sm">
                <Layers className="w-4 h-4" /> Step 3 of 6
              </div>
              <CardTitle className="text-xl sm:text-2xl font-bold font-display">Industry & Business Profile Selection</CardTitle>
              <CardDescription>
                Choose from 19 specialized sector templates. AccouPro automatically generates the appropriate Chart of Accounts, stock valuation method, and units of measure.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-5">
              {/* Filter & Search Bar */}
              <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-3">
                <div className="relative flex-1">
                  <Input
                    placeholder="Search industry by name, product or tag (e.g., scrap, steel, FMCG, pharmacy)..."
                    value={industrySearch}
                    onChange={(e) => setIndustrySearch(e.target.value)}
                  />
                </div>
                <div className="flex items-center gap-1.5 overflow-x-auto pb-1 sm:pb-0">
                  {INDUSTRY_CATEGORIES.map((cat) => (
                    <Button
                      key={cat}
                      type="button"
                      variant={selectedCategory === cat ? "default" : "outline"}
                      size="sm"
                      className="text-xs h-8 shrink-0 rounded-full"
                      onClick={() => setSelectedCategory(cat)}
                    >
                      {cat}
                    </Button>
                  ))}
                </div>
              </div>

              {/* Template Grid */}
              <ScrollArea className="h-[480px] pr-2">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3.5">
                  {filteredIndustries.map((tmpl) => {
                    const isSelected = selectedIndustryId === tmpl.id;
                    const Icon = getIndustryIcon(tmpl.icon);

                    return (
                      <div
                        key={tmpl.id}
                        onClick={() => setSelectedIndustryId(tmpl.id)}
                        className={`p-4 rounded-xl border transition-all cursor-pointer text-left space-y-2.5 ${
                          isSelected
                            ? "border-primary bg-primary/5 ring-2 ring-primary/20 shadow-md"
                            : "border-border/80 bg-card hover:border-primary/40 hover:bg-muted/30"
                        }`}
                      >
                        <div className="flex items-start justify-between gap-2">
                          <div className="flex items-center gap-2.5">
                            <div
                              className={`w-9 h-9 rounded-lg flex items-center justify-center ${
                                isSelected ? "bg-primary text-primary-foreground" : "bg-muted text-muted-foreground"
                              }`}
                            >
                              <Icon className="w-5 h-5" />
                            </div>
                            <div>
                              <h4 className="font-semibold text-sm text-foreground">{tmpl.name}</h4>
                              <span className="text-[11px] text-muted-foreground">{tmpl.category}</span>
                            </div>
                          </div>
                          {isSelected && (
                            <Badge className="bg-primary text-primary-foreground text-xs gap-1">
                              <Check className="w-3 h-3" /> Selected
                            </Badge>
                          )}
                        </div>

                        <p className="text-xs text-muted-foreground line-clamp-2 leading-relaxed">
                          {tmpl.description}
                        </p>

                        <div className="flex flex-wrap items-center gap-1.5 pt-1">
                          <Badge variant="outline" className="text-[10px] font-mono">
                            Units: {tmpl.defaultUnits.slice(0, 3).join(", ")}
                          </Badge>
                          <Badge variant="outline" className="text-[10px]">
                            Valuation: {tmpl.stockValuationMethod}
                          </Badge>
                          {tmpl.tags.slice(0, 2).map((t) => (
                            <Badge key={t} variant="secondary" className="text-[10px]">
                              {t}
                            </Badge>
                          ))}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </ScrollArea>

              {/* Selected Profile Highlight */}
              <div className="p-3.5 rounded-lg border border-primary/20 bg-primary/5 flex items-center justify-between">
                <div className="flex items-center gap-2.5">
                  <CheckCircle2 className="w-5 h-5 text-primary shrink-0" />
                  <div>
                    <span className="text-xs text-muted-foreground">Configured Template:</span>
                    <p className="text-sm font-bold text-foreground">{activeTemplate.name}</p>
                  </div>
                </div>
                <div className="text-right text-xs text-muted-foreground">
                  <span>{activeTemplate.stockGroups.length} Stock Groups</span> • <span>{activeTemplate.directExpenses.length + activeTemplate.indirectExpenses.length} Expense Ledgers</span>
                </div>
              </div>
            </CardContent>
            <CardFooter className="flex justify-between border-t border-border/60 pt-4">
              <Button variant="outline" onClick={handleBack} className="gap-2">
                <ArrowLeft className="w-4 h-4" />
                <span>Back</span>
              </Button>
              <Button onClick={handleNext} className="gap-2">
                <span>Preview Accounting Setup</span>
                <ArrowRight className="w-4 h-4" />
              </Button>
            </CardFooter>
          </Card>
        )}

        {/* STEP 4: AUTOMATIC ACCOUNTING CONFIGURATION PREVIEW */}
        {currentStep === 4 && (
          <Card className="border-border shadow-card">
            <CardHeader>
              <div className="flex items-center gap-2 text-primary font-semibold text-sm">
                <BookOpen className="w-4 h-4" /> Step 4 of 6
              </div>
              <CardTitle className="text-xl sm:text-2xl font-bold font-display">Automatic Accounting Configuration Preview</CardTitle>
              <CardDescription>
                AccouPro has tailored this Chart of Accounts for <strong>{activeTemplate.name}</strong>. Opening balances are strictly initialized to ₹0.00.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <Tabs defaultValue="chart-of-accounts" className="w-full">
                <TabsList className="grid grid-cols-3 mb-4">
                  <TabsTrigger value="chart-of-accounts">Chart of Accounts ({coaLedgers.filter((l) => l.enabled).length})</TabsTrigger>
                  <TabsTrigger value="inventory-groups">Inventory Groups ({activeTemplate.stockGroups.length})</TabsTrigger>
                  <TabsTrigger value="voucher-types">Default Voucher Types ({activeTemplate.defaultVoucherTypes.length})</TabsTrigger>
                </TabsList>

                {/* Tab 1: Chart of Accounts */}
                <TabsContent value="chart-of-accounts" className="space-y-4">
                  <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 p-3 rounded-lg bg-muted/30 border border-border">
                    <div className="text-xs text-muted-foreground">
                      Toggle ledgers you want enabled. All ledgers start with exactly <strong>₹0.00</strong> balance.
                    </div>
                    {/* Add Custom Ledger Dialog */}
                    <div className="flex items-center gap-2 w-full sm:w-auto">
                      <Input
                        placeholder="New ledger name..."
                        value={customLedgerName}
                        onChange={(e) => setCustomLedgerName(e.target.value)}
                        className="h-8 text-xs max-w-[180px]"
                      />
                      <Select value={customLedgerGroup} onValueChange={setCustomLedgerGroup}>
                        <SelectTrigger className="h-8 text-xs max-w-[140px]">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="Indirect Expenses">Indirect Exp</SelectItem>
                          <SelectItem value="Direct Expenses">Direct Exp</SelectItem>
                          <SelectItem value="Sundry Debtors">Debtor (Customer)</SelectItem>
                          <SelectItem value="Sundry Creditors">Creditor (Supplier)</SelectItem>
                          <SelectItem value="Bank Accounts">Bank Account</SelectItem>
                          <SelectItem value="Sales Accounts">Sales Account</SelectItem>
                        </SelectContent>
                      </Select>
                      <Button size="sm" variant="secondary" className="h-8 text-xs gap-1" onClick={handleAddCustomLedger}>
                        <Plus className="w-3.5 h-3.5" /> Add
                      </Button>
                    </div>
                  </div>

                  <ScrollArea className="h-[360px] pr-3">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-2.5">
                      {coaLedgers.map((ledger, idx) => (
                        <div
                          key={idx}
                          onClick={() => {
                            setCoaLedgers((prev) =>
                              prev.map((l, i) => (i === idx ? { ...l, enabled: !l.enabled } : l))
                            );
                          }}
                          className={`p-3 rounded-lg border transition-colors flex items-center justify-between cursor-pointer ${
                            ledger.enabled
                              ? "bg-card border-border hover:bg-muted/40"
                              : "bg-muted/20 border-border/40 opacity-60"
                          }`}
                        >
                          <div className="flex items-center gap-2.5">
                            {ledger.enabled ? (
                              <CheckSquare className="w-4 h-4 text-primary shrink-0" />
                            ) : (
                              <Square className="w-4 h-4 text-muted-foreground shrink-0" />
                            )}
                            <div>
                              <p className="text-sm font-semibold text-foreground flex items-center gap-1.5">
                                {ledger.name}
                                {ledger.isCustom && (
                                  <Badge variant="outline" className="text-[9px] py-0 px-1 bg-amber-500/10 text-amber-600 border-amber-500/30">
                                    Custom
                                  </Badge>
                                )}
                              </p>
                              <span className="text-[11px] text-muted-foreground">{ledger.group}</span>
                            </div>
                          </div>
                          <div className="text-right">
                            <span className="text-xs font-mono font-medium text-muted-foreground">₹0.00 {ledger.balanceType}</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </ScrollArea>
                </TabsContent>

                {/* Tab 2: Inventory Groups */}
                <TabsContent value="inventory-groups" className="space-y-4">
                  <div className="p-4 rounded-xl border border-border bg-card space-y-4">
                    <div>
                      <h4 className="text-sm font-semibold text-foreground">Tailored Stock Groups & Categories</h4>
                      <p className="text-xs text-muted-foreground">
                        Configured specifically for {activeTemplate.name} operations.
                      </p>
                    </div>

                    <div className="space-y-3">
                      <div>
                        <Label className="text-xs font-semibold text-muted-foreground uppercase">Stock Groups</Label>
                        <div className="flex flex-wrap gap-2 mt-1.5">
                          {activeTemplate.stockGroups.map((g) => (
                            <Badge key={g} variant="outline" className="text-xs py-1 px-3">
                              <Package className="w-3 h-3 mr-1 text-primary" /> {g}
                            </Badge>
                          ))}
                        </div>
                      </div>

                      <div>
                        <Label className="text-xs font-semibold text-muted-foreground uppercase">Stock Categories</Label>
                        <div className="flex flex-wrap gap-2 mt-1.5">
                          {activeTemplate.stockCategories.map((c) => (
                            <Badge key={c} variant="secondary" className="text-xs py-1 px-3">
                              {c}
                            </Badge>
                          ))}
                        </div>
                      </div>

                      <div>
                        <Label className="text-xs font-semibold text-muted-foreground uppercase">Standard Units of Measure</Label>
                        <div className="flex flex-wrap gap-2 mt-1.5">
                          {activeTemplate.defaultUnits.map((u) => (
                            <Badge key={u} className="text-xs font-mono py-1 px-2.5 bg-primary/10 text-primary border-primary/20">
                              {u}
                            </Badge>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>
                </TabsContent>

                {/* Tab 3: Voucher Types */}
                <TabsContent value="voucher-types" className="space-y-4">
                  <div className="p-4 rounded-xl border border-border bg-card space-y-3">
                    <div>
                      <h4 className="text-sm font-semibold text-foreground">Active Voucher Types</h4>
                      <p className="text-xs text-muted-foreground">
                        Standard double-entry transaction types pre-configured for your accounting cycle.
                      </p>
                    </div>
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-2.5">
                      {activeTemplate.defaultVoucherTypes.map((vt) => (
                        <div key={vt} className="p-2.5 rounded-lg border border-border bg-muted/20 text-center">
                          <span className="text-xs font-semibold text-foreground">{vt}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </TabsContent>
              </Tabs>
            </CardContent>
            <CardFooter className="flex justify-between border-t border-border/60 pt-4">
              <Button variant="outline" onClick={handleBack} className="gap-2">
                <ArrowLeft className="w-4 h-4" />
                <span>Back</span>
              </Button>
              <Button onClick={handleNext} className="gap-2">
                <span>Continue to Team Setup</span>
                <ArrowRight className="w-4 h-4" />
              </Button>
            </CardFooter>
          </Card>
        )}

        {/* STEP 5: TEAM & ACCESS SETUP (OPTIONAL) */}
        {currentStep === 5 && (
          <Card className="border-border shadow-card">
            <CardHeader>
              <div className="flex items-center gap-2 text-primary font-semibold text-sm">
                <UserPlus className="w-4 h-4" /> Step 5 of 6 (Optional)
              </div>
              <CardTitle className="text-xl sm:text-2xl font-bold font-display">Team & Role-Based Access Setup</CardTitle>
              <CardDescription>
                Invite your primary accountant, tax consultant, or billing team. You can skip this step if you are a solo entrepreneur.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-5">
              <div className="flex items-center justify-between p-3.5 rounded-lg border border-primary/20 bg-primary/5">
                <div className="flex items-center gap-2.5">
                  <Users className="w-5 h-5 text-primary" />
                  <div>
                    <p className="text-sm font-semibold text-foreground">Need to set up alone?</p>
                    <p className="text-xs text-muted-foreground">You can invite team members anytime later from Settings &rarr; Users.</p>
                  </div>
                </div>
                <Button variant="outline" size="sm" onClick={handleNext} className="text-xs font-medium">
                  Skip & Set Up Later
                </Button>
              </div>

              {teamMembers.length === 0 ? (
                <div className="text-center py-8 border border-dashed border-border rounded-xl space-y-3">
                  <div className="w-10 h-10 rounded-full bg-muted flex items-center justify-center mx-auto text-muted-foreground">
                    <UserPlus className="w-5 h-5" />
                  </div>
                  <div>
                    <p className="text-sm font-medium text-foreground">No team members invited yet</p>
                    <p className="text-xs text-muted-foreground">Invite your chartered accountant, bookkeeper, or sales executive.</p>
                  </div>
                  <Button variant="secondary" size="sm" onClick={handleAddTeamMember} className="gap-1.5">
                    <Plus className="w-4 h-4" /> Add Team Member
                  </Button>
                </div>
              ) : (
                <div className="space-y-3">
                  {teamMembers.map((member, i) => (
                    <div key={i} className="p-3.5 rounded-xl border border-border bg-card flex flex-col md:flex-row items-stretch md:items-center gap-3">
                      <div className="flex-1 grid grid-cols-1 sm:grid-cols-3 gap-2.5">
                        <Input
                          placeholder="Member Name (e.g. Suresh Patel)"
                          value={member.name}
                          onChange={(e) => handleUpdateTeamMember(i, "name", e.target.value)}
                        />
                        <Input
                          type="email"
                          placeholder="Email (e.g. suresh@ca-associates.in)"
                          value={member.email}
                          onChange={(e) => handleUpdateTeamMember(i, "email", e.target.value)}
                        />
                        <Select
                          value={member.role}
                          onValueChange={(val) => handleUpdateTeamMember(i, "role", val)}
                        >
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="accountant">Accountant (Ledgers, GST, Vouchers)</SelectItem>
                            <SelectItem value="company_admin">Company Admin (Full Access)</SelectItem>
                            <SelectItem value="sales">Sales (Billing & Invoices)</SelectItem>
                            <SelectItem value="purchase">Purchase (Orders & Vendors)</SelectItem>
                            <SelectItem value="inventory">Inventory (Stock & Warehouse)</SelectItem>
                            <SelectItem value="auditor">Auditor (Read-Only Financials)</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => handleRemoveTeamMember(i)}
                        className="text-destructive hover:bg-destructive/10 self-end md:self-center shrink-0"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  ))}

                  <Button variant="outline" size="sm" onClick={handleAddTeamMember} className="gap-1.5">
                    <Plus className="w-4 h-4" /> Add Another Member
                  </Button>
                </div>
              )}
            </CardContent>
            <CardFooter className="flex justify-between border-t border-border/60 pt-4">
              <Button variant="outline" onClick={handleBack} className="gap-2">
                <ArrowLeft className="w-4 h-4" />
                <span>Back</span>
              </Button>
              <Button onClick={handleNext} className="gap-2">
                <span>Proceed to Final Review</span>
                <ArrowRight className="w-4 h-4" />
              </Button>
            </CardFooter>
          </Card>
        )}

        {/* STEP 6: FINAL REVIEW & ONE-CLICK LAUNCH */}
        {currentStep === 6 && (
          <Card className="border-border shadow-card">
            <CardHeader>
              <div className="flex items-center gap-2 text-primary font-semibold text-sm">
                <CheckCircle2 className="w-4 h-4" /> Step 6 of 6
              </div>
              <CardTitle className="text-xl sm:text-2xl font-bold font-display">Review & One-Click Workspace Launch</CardTitle>
              <CardDescription>
                Confirm your business identity and accounting profile. Clicking complete will initialize your production database idempotently.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Summary Card 1: Business Identity */}
                <div className="p-4 rounded-xl border border-border bg-card space-y-3">
                  <div className="flex items-center justify-between">
                    <h4 className="text-sm font-semibold text-foreground flex items-center gap-2">
                      <Building2 className="w-4 h-4 text-primary" /> Business Identity
                    </h4>
                    <Button variant="ghost" size="sm" className="h-6 text-xs text-primary" onClick={() => setCurrentStep(1)}>
                      Edit
                    </Button>
                  </div>
                  <div className="text-xs space-y-1.5">
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Legal Name:</span>
                      <strong className="text-foreground">{identityData.name}</strong>
                    </div>
                    {identityData.tradeName && (
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Trade Name:</span>
                        <span className="text-foreground">{identityData.tradeName}</span>
                      </div>
                    )}
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Constitution:</span>
                      <span className="text-foreground">{identityData.businessConstitution}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">State:</span>
                      <span className="text-foreground">{identityData.state} (Code {identityData.stateCode})</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">City & PIN:</span>
                      <span className="text-foreground">{identityData.city || "-"} - {identityData.pincode || "-"}</span>
                    </div>
                  </div>
                </div>

                {/* Summary Card 2: Tax & Compliance */}
                <div className="p-4 rounded-xl border border-border bg-card space-y-3">
                  <div className="flex items-center justify-between">
                    <h4 className="text-sm font-semibold text-foreground flex items-center gap-2">
                      <FileText className="w-4 h-4 text-primary" /> Tax & Compliance
                    </h4>
                    <Button variant="ghost" size="sm" className="h-6 text-xs text-primary" onClick={() => setCurrentStep(2)}>
                      Edit
                    </Button>
                  </div>
                  <div className="text-xs space-y-1.5">
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">GST Status:</span>
                      <Badge variant={taxData.gstRegistered ? "default" : "secondary"} className="text-[10px]">
                        {taxData.gstRegistered ? "GST Registered" : "Non-GST"}
                      </Badge>
                    </div>
                    {taxData.gstRegistered && (
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">GSTIN:</span>
                        <strong className="font-mono text-foreground">{taxData.gstin}</strong>
                      </div>
                    )}
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">PAN:</span>
                      <span className="font-mono text-foreground">{taxData.pan || "-"}</span>
                    </div>
                    {taxData.compositionScheme && (
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Scheme:</span>
                        <Badge variant="outline" className="text-[10px]">Composition Scheme</Badge>
                      </div>
                    )}
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Head Office Branch:</span>
                      <span className="text-foreground">HO-01 (Auto-Configured)</span>
                    </div>
                  </div>
                </div>

                {/* Summary Card 3: Industry & Accounting Profile */}
                <div className="p-4 rounded-xl border border-border bg-card space-y-3">
                  <div className="flex items-center justify-between">
                    <h4 className="text-sm font-semibold text-foreground flex items-center gap-2">
                      <Layers className="w-4 h-4 text-primary" /> Industry Profile
                    </h4>
                    <Button variant="ghost" size="sm" className="h-6 text-xs text-primary" onClick={() => setCurrentStep(3)}>
                      Edit
                    </Button>
                  </div>
                  <div className="text-xs space-y-1.5">
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Selected Industry:</span>
                      <strong className="text-primary">{activeTemplate.name}</strong>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Valuation Method:</span>
                      <span className="text-foreground">{activeTemplate.stockValuationMethod}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Measurement Units:</span>
                      <span className="font-mono text-foreground">{activeTemplate.defaultUnits.slice(0, 4).join(", ")}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Stock Groups:</span>
                      <span className="text-foreground">{activeTemplate.stockGroups.length} Categories</span>
                    </div>
                  </div>
                </div>

                {/* Summary Card 4: Chart of Accounts */}
                <div className="p-4 rounded-xl border border-border bg-card space-y-3">
                  <div className="flex items-center justify-between">
                    <h4 className="text-sm font-semibold text-foreground flex items-center gap-2">
                      <BookOpen className="w-4 h-4 text-primary" /> Chart of Accounts
                    </h4>
                    <Button variant="ghost" size="sm" className="h-6 text-xs text-primary" onClick={() => setCurrentStep(4)}>
                      Edit
                    </Button>
                  </div>
                  <div className="text-xs space-y-1.5">
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Enabled Ledgers:</span>
                      <strong className="text-foreground">{coaLedgers.filter((l) => l.enabled).length} Accounts</strong>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Opening Balances:</span>
                      <strong className="text-foreground font-mono">₹0.00 (Ready for Entries)</strong>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Voucher Types:</span>
                      <span className="text-foreground">{activeTemplate.defaultVoucherTypes.length} Types</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Team Invitations:</span>
                      <span className="text-foreground">{teamMembers.filter((m) => m.email).length} Members</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Clean Accounting Workspace Info Card */}
              <div className="p-4 rounded-xl border border-primary/20 bg-muted/40 flex items-start gap-3">
                <ShieldCheck className="w-5 h-5 text-primary shrink-0 mt-0.5" />
                <div className="text-xs leading-relaxed text-muted-foreground">
                  <strong className="text-foreground font-semibold block mb-0.5">
                    Workspace Initialization
                  </strong>
                  Your workspace will start with a customized chart of accounts tailored to your industry, ready for recording invoices, purchases, payments, and receipts.
                </div>
              </div>
            </CardContent>
            <CardFooter className="flex justify-between border-t border-border/60 pt-4">
              <Button variant="outline" onClick={handleBack} disabled={submitting} className="gap-2">
                <ArrowLeft className="w-4 h-4" />
                <span>Back</span>
              </Button>
              <Button
                onClick={handleCompleteOnboarding}
                disabled={submitting}
                className="gap-2 bg-primary text-primary-foreground font-semibold px-6 shadow-md"
              >
                {submitting ? (
                  <>
                    <RefreshCw className="w-4 h-4 animate-spin" />
                    <span>Configuring Workspace...</span>
                  </>
                ) : (
                  <>
                    <Sparkles className="w-4 h-4" />
                    <span>Complete Setup & Launch AccouPro</span>
                  </>
                )}
              </Button>
            </CardFooter>
          </Card>
        )}

      </div>
    </div>
  );
}